import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Flex,
  VStack,
  SimpleGrid,
  Avatar,
  useColorModeValue,
  Button,
  Spinner,
  Skeleton,
  Badge,
} from '@chakra-ui/react';
import { FiSmartphone } from 'react-icons/fi';
import { AiOutlineMobile } from 'react-icons/ai';
import API from '../../context/API';

const ActiveSessionsDevices = () => {
  const [merchantData, setMerchantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const tileBg = useColorModeValue('gray.100', 'gray.700');
  const tileHoverBg = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');

  const fetchMerchantData = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('merchant/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMerchantData(response.data.data);
      const now = Date.now();
      localStorage.setItem('lastUpdatedTimestamp', now.toString());
    } catch (error) {
      console.error('Error fetching merchant data:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchantData();
  }, []);

  if (loading) {
    return (
      <Box mt={10} textAlign="center">
        <Spinner size="xl" color="teal.500" />
        <Text mt={4}>Loading Active Sessions & Devices...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={10} textAlign="center">
        <Text color="red.500" fontWeight="bold">
          Failed to fetch data. Please try again later.
        </Text>
        <Button mt={4} onClick={fetchMerchantData} colorScheme="teal">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box mt={10}>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontSize="2xl" fontWeight="bold">
          Active Sessions & Devices
        </Text>
        <Button colorScheme="teal" onClick={fetchMerchantData}>
          Refresh
        </Button>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {/* Current Sessions */}
        <Box bg={tileBg} p={6} borderRadius="md" boxShadow="lg">
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Current Sessions
          </Text>
          <VStack align="stretch" spacing={4}>
            {merchantData?.currentSessions.map((session) => (
              <Flex
                key={session.id}
                align="center"
                p={4}
                bg={bgColor}
                borderRadius="md"
                _hover={{ bg: tileHoverBg }}
              >
                <Avatar icon={<AiOutlineMobile />} mr={4} />
                <Box>
                  <Text fontWeight="bold">{session.deviceName}</Text>
                  <Text fontSize="sm" color={subTextColor}>
                    OS: {session.deviceOs} | IP: {session.deviceIp}
                  </Text>
                  <Badge colorScheme="green" mt={1}>
                    Active
                  </Badge>
                </Box>
              </Flex>
            ))}
          </VStack>
        </Box>

        {/* Devices */}
        <Box bg={tileBg} p={6} borderRadius="md" boxShadow="lg">
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Devices
          </Text>
          <VStack align="stretch" spacing={4}>
            {merchantData?.devices.map((device) => (
              <Flex
                key={device.id}
                align="center"
                p={4}
                bg={bgColor}
                borderRadius="md"
                _hover={{ bg: tileHoverBg }}
              >
                <Avatar icon={<FiSmartphone />} mr={4} />
                <Box>
                  <Text fontWeight="bold">{device.name}</Text>
                  <Text fontSize="sm" color={subTextColor}>
                    Type: {device.type.name} | ID: {device.identifier}
                  </Text>
                  <Badge colorScheme="blue" mt={1}>
                    Registered
                  </Badge>
                </Box>
              </Flex>
            ))}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default ActiveSessionsDevices;
